import React, { useEffect, useRef, useState } from "react";
import useLatestRequest from "../utils/useLatestRequest";
import {
  Table,
  Spin,
  Space,
  Select,
  DatePicker,
  Button,
  notification,
  InputNumber,
  message,
} from "antd";
import formatIndianNumber from "../../../../../utils/formatIndianNumber";
import { DownloadOutlined } from "@ant-design/icons";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import moment from "moment";
import {
  getCurrentFinancialYear,
  getFinancialYearOptions,
} from "../utils/financialYearUtils";
import { COMPANY_CATEGORIES } from "../../../../../utils/companyCategories";

const { Option } = Select;

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const DATE_FORMAT = "DD-MM-YYYY";

const TopCustomerReport = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [limit, setLimit] = useState(10);
  const [inputLimit, setInputLimit] = useState(10);
  const [downloading, setDownloading] = useState(false);
  const [clientCategory, setClientCategory] = useState(null);
  const [salesmen, setSalesmen] = useState([]);
  const [selectedSalesman, setSelectedSalesman] = useState(null);
  const [salesmenLoading, setSalesmenLoading] = useState(true);
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const debounceRef = useRef();


   useEffect(() => {
    setInputLimit(limit);
  }, [limit]);


     const handleLimitChange = (v) => {
    setInputLimit(v); // update input immediately
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setLimit(Number(v) || 0);
    }, 400);
  };


  useEffect(() => {
    const fetchSalesmen = async () => {
      setSalesmenLoading(true);
      try {
        const response = await fetch(
          `${apiBaseUrl}/inventoryManagement/listSalesPersons?page=1&limit=50`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const result = await response.json();

        if (result.success && Array.isArray(result.data)) {
          const seen = new Set();
          const opts = result.data
            .map((s) => String(s?.name ?? "").trim())
            .filter((name) => {
              if (!name) return false;
              if (seen.has(name)) return false;
              seen.add(name);
              return true;
            })
            .sort((a, b) => a.localeCompare(b))
            .map((name) => ({ label: name, value: name }));
          setSalesmen(opts);
        } else {
          console.error("Failed to fetch salesmen:", result.message);
          message.error("Failed to fetch salesmen data");
        }
      } catch (error) {
        console.error("Error fetching salesmen:", error);
        message.error("Error fetching salesmen data");
      } finally {
        setSalesmenLoading(false);
      }
    };

    fetchSalesmen();
  }, [apiBaseUrl]);
  const beginRequest = useLatestRequest();

  const fetchTopTen = async () => {
    const isCurrent = beginRequest();
    const token = window.localStorage.getItem("token");
    const params = new URLSearchParams();
    params.set("financialYear", financialYear);
    if (limit) params.set("limit", limit);
    if (dateRange && dateRange.length === 2) {
      params.set("startDate", dateRange[0].format(DATE_FORMAT));
      params.set("endDate", dateRange[1].format(DATE_FORMAT));
    }
    if (selectedSalesman) params.set("createdBy", selectedSalesman);
    if (clientCategory) params.set("clientCategory", clientCategory);
    const query = params.toString() ? `?${params.toString()}` : "";
    try {
      setLoading(true);
      const res = await fetch(
        `${apiBaseUrl}/inventoryManagement/getTopCustomers${query}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            authorization: `Token ${token}`,
          },
        },
      );
      const result = await res.json();
      if (!isCurrent()) return;
      if (result.success && Array.isArray(result.data)) {
        setList(result.data);
      } else {
        setList([]);
        notification.warning({
          message: "No data",
          description: result.message || "No customers found.",
        });
      }
    } catch (err) {
      if (!isCurrent()) return;
      console.error(err);
      setList([]);
      notification.error({
        message: "Error",
        description: "Failed to fetch report.",
      });
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };
  const handleDownloadExcel = async () => {
    try {
      setDownloading(true);
      const token = window.localStorage.getItem("token");
      const params = new URLSearchParams();
      params.set("financialYear", financialYear);
      if (limit) params.set("limit", limit);
      if (dateRange && dateRange.length === 2) {
        params.set("startDate", dateRange[0].format(DATE_FORMAT));
        params.set("endDate", dateRange[1].format(DATE_FORMAT));
      }
      if (clientCategory) params.set("clientCategory", clientCategory);
      if (selectedSalesman) params.set("createdBy", selectedSalesman);
      params.set("excel", "true");
      const query = params.toString() ? `?${params.toString()}` : "";

      const res = await fetch(
        `${apiBaseUrl}/inventoryManagement/getTopCustomers${query}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            authorization: `Token ${token}`,
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream, */*",
          },
        },
      );

      if (!res.ok) {
        // try parse json error
        let errText = "Failed to download";
        try {
          const j = await res.json();
          errText = j.message || errText;
        } catch (e) {}
        notification.error({ message: "Error", description: errText });
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Top_Customers_${financialYear}_${moment().format("YYYY-MM-DD")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Error",
        description: "Failed to download excel.",
      });
    } finally {
      setDownloading(false);
    }
  };
  useEffect(() => {
    fetchTopTen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, limit, clientCategory, selectedSalesman]);

  const columns = [
    {
      title: "Sr.No",
      key: "rank",
      width: 60,
      render: (_, __, idx) => idx + 1,
      align: "center",
    },
    {
      title: "Customer",
      dataIndex: "name",
      key: "name",
      align: "center",
      render: (t) => t || "-",
    },
    {
      title: "Share %",
      key: "share",
      align: "center",
      render: (_, row) => {
        const totalSum = list.reduce((s, r) => s + (Number(r.total) || 0), 0);
        if (!totalSum) return "0.00%";
        const share = ((Number(row.total) || 0) / totalSum) * 100;
        return `${share.toFixed(2)}%`;
      },
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      align: "center",
      render: (amt) => (amt != null ? `₹ ${formatIndianNumber(amt)}` : "-"),
    },
  ];
  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
          }}
        />
      ),
    },
  };
  return (
    <div
      className="parent-padding-container"
      style={{ backgroundColor: "white", borderRadius: 12, padding: 20 }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
          <h5 style={{ margin: 0 }}>Top Customers</h5>
        </div>

        <Space align="center" wrap>
          <label htmlFor="topCustomers">Top Customers:</label>
          <InputNumber
            id="topCustomers"
            min={1}
            value={inputLimit}
            onChange={handleLimitChange}
            style={{ width: 120 }}
            placeholder="Top N"
          />
          <Select
            value={financialYear}
            onChange={setFinancialYear}
            style={{ width: 120 }}
            placeholder="Select FY"
          >
            {getFinancialYearOptions().map((fy) => (
              <Option key={fy} value={fy}>
                {fy}
              </Option>
            ))}
          </Select>
          <Select
            value={selectedSalesman}
            onChange={setSelectedSalesman}
            style={{ width: 200 }}
            placeholder="Filter by Salesman"
            loading={salesmenLoading}
            allowClear
            showSearch
            filterOption={(input, option) =>
              (() => {
                const label = String(option?.label ?? "").toLowerCase();
                const query = String(input ?? "").toLowerCase();
                if (!query) return true;
                return label.startsWith(query);
              })()
            }
            options={salesmen}
          />

          <Select
            value={clientCategory}
            onChange={(v) => setClientCategory(v)}
            allowClear
            placeholder="Client Category"
            style={{ width: 160 }}
          >
            {COMPANY_CATEGORIES.map((category) => (
              <Select.Option key={category} value={category}>
                {category}
              </Select.Option>
            ))}
          </Select>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={(d) => setDateRange(d)}
            format={DATE_FORMAT}
            allowClear
            style={{ minWidth: 260 }}
          />
          <Button
            type="primary"
            icon={<FaDownload />}
            onClick={handleDownloadExcel}
            loading={downloading}
          >
            Download Excel
          </Button>
        </Space>
      </div>

      <Spin spinning={loading}>
        <Table
          dataSource={list}
          columns={columns}
          rowKey={(r) => r.name}
          components={components}
          scroll={{ y: 500 }}
          pagination={false}
          sticky
        />
      </Spin>
    </div>
  );
};

export default TopCustomerReport;
